import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import toast from 'react-hot-toast';

const generateSampleNotifications = (userName) => {
  const now = new Date();
  const today = [];
  const thisWeek = [];

  // Today's notifications
  today.push({
    id: 1,
    type: 'event',
    emoji: '🧘',
    title: 'Morning Yoga Tomorrow',
    message: 'Yoga session at Community Park, Sector 14 at 7:00 AM. Don\'t forget to carry your mat!',
    time: '2 hours ago',
    read: false,
    color: '#9C27B0',
    bg: '#F3E5F5'
  });

  today.push({
    id: 2,
    type: 'health',
    emoji: '❤️',
    title: 'Log Your Health Today',
    message: 'You haven\'t logged your blood pressure today. Track daily for best results.',
    time: '4 hours ago',
    read: false,
    color: '#F44336',
    bg: '#FFEBEE'
  });

  today.push({
    id: 3,
    type: 'community',
    emoji: '💬',
    title: 'New message in Community',
    message: 'Ramesh Ji: "Good morning everyone! Beautiful weather today, perfect for a walk 🌞"',
    time: '5 hours ago',
    read: true,
    color: '#2196F3',
    bg: '#E3F2FD'
  });

  today.push({
    id: 4,
    type: 'service',
    emoji: '✅',
    title: 'Service Request Confirmed',
    message: 'Your Doctor Visit request has been confirmed for tomorrow 10 AM.',
    time: '6 hours ago',
    read: true,
    color: '#4CAF50',
    bg: '#E8F5E9'
  });

  // This week's notifications
  thisWeek.push({
    id: 5,
    type: 'event',
    emoji: '☕',
    title: 'Senior Tea Meetup',
    message: 'You are registered for Senior Tea Meetup on Sunday at 4 PM. See you there!',
    time: '2 days ago',
    read: true,
    color: '#795548',
    bg: '#EFEBE9'
  });

  thisWeek.push({
    id: 6,
    type: 'health',
    emoji: '🏥',
    title: 'Free Health Camp Reminder',
    message: 'Free BP and sugar checkup by Dr. Sharma this Wednesday at Community Hall, Block B.',
    time: '3 days ago',
    read: true,
    color: '#FF6B35',
    bg: '#FFF3EE'
  });

  thisWeek.push({
    id: 7,
    type: 'community',
    emoji: '🎉',
    title: `Welcome to SaathiCircle!`,
    message: `Hi ${userName || 'there'}! Welcome to our senior community. Start by joining an event or saying hi in the community chat.`,
    time: '5 days ago',
    read: true,
    color: '#FF6B35',
    bg: '#FFF3EE'
  });

  thisWeek.push({
    id: 8,
    type: 'event',
    emoji: '🎵',
    title: 'Bhajan Evening This Friday',
    message: 'Devotional music and bhajans together at Temple Road, near Gate 3 at 6 PM.',
    time: '6 days ago',
    read: true,
    color: '#FF9800',
    bg: '#FFF3E0'
  });

  return { today, thisWeek };
};

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState(() => generateSampleNotifications(user?.name));

  const unreadCount = [...notifications.today, ...notifications.thisWeek].filter(n => !n.read).length;

  const markAllRead = () => {
    setNotifications({
      today: notifications.today.map(n => ({ ...n, read: true })),
      thisWeek: notifications.thisWeek.map(n => ({ ...n, read: true }))
    });
    toast.success('All notifications marked as read');
  };

  const markRead = (id) => {
    setNotifications({
      today: notifications.today.map(n => n.id === id ? { ...n, read: true } : n),
      thisWeek: notifications.thisWeek.map(n => n.id === id ? { ...n, read: true } : n)
    });
  };

  const handleNotificationClick = (notif) => {
    markRead(notif.id);
    if (notif.type === 'event') navigate('/events');
    else if (notif.type === 'health') navigate('/health');
    else if (notif.type === 'community') navigate('/community');
    else if (notif.type === 'service') navigate('/services');
  };

  const NotificationItem = ({ notif }) => (
    <div
      onClick={() => handleNotificationClick(notif)}
      style={{
        display: 'flex',
        gap: '12px',
        padding: '14px',
        background: notif.read ? 'white' : `${notif.bg}`,
        borderRadius: '14px',
        marginBottom: '8px',
        cursor: 'pointer',
        border: `2px solid ${notif.read ? '#F0E8E0' : notif.color + '30'}`,
        transition: 'all 0.2s',
        position: 'relative'
      }}
    >
      {/* Unread dot */}
      {!notif.read && (
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: notif.color
        }} />
      )}

      {/* Icon */}
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: notif.bg,
        border: `2px solid ${notif.color}30`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '22px',
        flexShrink: 0
      }}>
        {notif.emoji}
      </div>

      {/* Content */}
      <div style={{ flex: 1, paddingRight: notif.read ? '0' : '16px' }}>
        <div style={{
          fontSize: '15px',
          fontWeight: notif.read ? '600' : '700',
          color: '#2D2D2D',
          marginBottom: '3px',
          lineHeight: '1.3'
        }}>
          {notif.title}
        </div>
        <div style={{ fontSize: '13px', color: '#777', lineHeight: '1.5', marginBottom: '4px' }}>
          {notif.message}
        </div>
        <div style={{ fontSize: '12px', color: '#bbb' }}>{notif.time}</div>
      </div>
    </div>
  );

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #FF9800 0%, #FFA726 100%)',
        padding: '16px',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', color: 'white', cursor: 'pointer', fontSize: '18px' }}
            >
              ←
            </button>
            <div>
              <h2 style={{ color: 'white', fontSize: '18px', marginBottom: '2px' }}>
                🔔 Notifications
              </h2>
              {unreadCount > 0 && (
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '20px',
                padding: '6px 12px',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Hind', 'Poppins', sans-serif"
              }}
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        {/* Today section */}
        {notifications.today.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '700',
              color: '#888',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '10px',
              paddingLeft: '4px'
            }}>
              Today
            </div>
            {notifications.today.map(notif => (
              <NotificationItem key={notif.id} notif={notif} />
            ))}
          </div>
        )}

        {/* This Week section */}
        {notifications.thisWeek.length > 0 && (
          <div>
            <div style={{
              fontSize: '13px',
              fontWeight: '700',
              color: '#888',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '10px',
              paddingLeft: '4px',
              marginTop: '8px'
            }}>
              This Week
            </div>
            {notifications.thisWeek.map(notif => (
              <NotificationItem key={notif.id} notif={notif} />
            ))}
          </div>
        )}

        {notifications.today.length === 0 && notifications.thisWeek.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔔</div>
            <div className="empty-title">All caught up!</div>
            <div className="empty-text">No new notifications right now</div>
          </div>
        )}

        {/* Notification settings hint */}
        <div className="card" style={{ marginTop: '16px', background: '#FFF8F0', textAlign: 'center' }}>
          <p style={{ fontSize: '13px', color: '#888' }}>
            💡 Notifications include event reminders, community updates, and health alerts
          </p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Notifications;
